import { OrganizationInviteSchema, OrganizationSchema } from "@/api-client";
import { Urls } from "@/domain/urls";
import { AccountBalanceRounded } from "@mui/icons-material";
import { Avatar, Badge, Link, Stack, Tooltip, Typography } from "@mui/material";
import React from "react";
import { Link as RouterLink } from "react-router-dom";

type BaseProps = {
  type: "members" | "invites";
  orgAdmin: boolean;
};

type MemberProps = BaseProps & {
  type: "members";
  user: {
    profile: {
      avatarUrl?: string;
    };
    username: string;
    fullName?: string;
    preferredName?: string;
  };
};

type InviteProps = BaseProps & {
  type: "invites";
  email: string;
};

type Props = MemberProps | InviteProps;

export const getAvatarCell = (props: Props): React.ReactElement => {
  return (
    <Stack direction="row" alignItems={"center"}>
      <Badge
        overlap="circular"
        sx={{
          "& .MuiBadge-badge": {
            padding: 0,
            margin: 0,
            backgroundColor: (theme) => theme.palette.grey[200],
            border: (theme) => `1px solid ${theme.palette.background.paper}`,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        badgeContent={
          <Tooltip title="Organization Admin" placement="top">
            <AccountBalanceRounded sx={{ fontSize: "12px" }} color="primary" />
          </Tooltip>
        }
        invisible={!props.orgAdmin}
      >
        <Avatar
          sizes="small"
          sx={{ marginRight: 1 }}
          src={
            props.type === "members" ? props.user.profile.avatarUrl : undefined
          }
          alt={props.type === "members" ? props.user.username : undefined}
        />
      </Badge>
      <Stack>
        <Typography variant="h3" noWrap>
          {props.type === "members" && `${props.user.fullName}`}
          {props.type === "invites" && props.email}
        </Typography>
        <Typography
          variant="subtitle2"
          noWrap
          color={props.type === "invites" ? "error" : undefined}
        >
          {props.type === "members" && `${props.user.username}`}
          {props.type === "invites" && "Invitation not yet accepted"}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const getUsedSeats = (
  organization?: OrganizationSchema,
  orgInvites?: OrganizationInviteSchema[],
): number => {
  return (orgInvites?.length || 0) + (organization?.memberSet.length || 0);
};

export const haveReachedMemberLimit = (
  organization?: OrganizationSchema,
  orgInvites?: OrganizationInviteSchema[],
): boolean => {
  const usedSeats = getUsedSeats(organization, orgInvites);
  const memberLimit = organization?.memberLimit || 0;

  return usedSeats + 1 > memberLimit;
};

export const seatLimitAlertText = (
  organization?: OrganizationSchema,
  orgInvites?: OrganizationInviteSchema[],
): React.ReactNode | null => {
  const usedSeats =
    (orgInvites?.length || 0) + (organization?.memberSet.length || 0);
  const memberLimit = organization?.memberLimit || 0;

  const alertText =
    organization && usedSeats >= memberLimit ? (
      <>
        Your organization is using {usedSeats} of {memberLimit} allowed seats
        (see{" "}
        <Link component={RouterLink} to={`/${Urls.Team}?tab=Team`}>
          currently used seats
        </Link>
        ). Please{" "}
        <Link component={RouterLink} to={`/${Urls.Pricing}`}>
          upgrade your plan
        </Link>{" "}
        to enable adding more members.
      </>
    ) : null;

  return alertText;
};
